import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Zap, ArrowRight } from "lucide-react";
import { DEEPSEEK_PRICING } from "@/lib/deepseek-pricing";

// Single source of truth: the changelog may never claim the peak-hour
// surcharge is live while peakSurcharge.applies is false on /pricing.
const { announced, applies } = DEEPSEEK_PRICING.peakSurcharge;
const surchargeLine = applies
  ? "A peak-hour surcharge is now active: peak rates apply Monday–Friday 01:00–04:00 and 06:00–10:00 UTC, and off-peak rates are half of peak."
  : announced
    ? "A peak-hour surcharge (UTC+8) has been announced but is not active yet — the official rate card still shows one flat tier."
    : "No peak-hour surcharge is in effect; the official rate card shows one flat tier.";

const updates = [
  {
    date: "2026-09-09",
    title: "DeepSeek-V4.1-Flash is generally available — and peak pricing goes live",
    summary:
      `552B-parameter MoE with a causal encoder–decoder split and native image understanding, served as deepseek-flash. V4-Flash and V4-Flash-Vision-Exp are retired and their names route to V4.1-Flash; V4-Pro API service continues after 14 September with unchanged billing. ${surchargeLine}`,
    href: "/blog/deepseek-v41-flash-generally-available",
  },
  {
    date: "2026-07-31",
    title: "DeepSeek-V4-Flash official API in public beta",
    summary:
      "Same model string, same architecture, new post-training: Terminal Bench 2.1 82.7 and DeepSWE 54.4 beat V4-Pro-Preview. Native Responses API and Codex support. V4-Pro and the app are unchanged.",
    href: "/blog/deepseek-v4-flash-ga-agent-benchmarks",
  },
  {
    date: "2026-07-24",
    title: "Legacy API aliases retired — surcharge still not active",
    summary:
      `Legacy deepseek-chat and deepseek-reasoner aliases were retired; migrate to deepseek-v4-flash. ${surchargeLine}`,
    href: "/blog/deepseek-v4-ga-surge-pricing-migration",
  },
  {
    date: "2026-07-03",
    title: "Inside DSpark: semi-autoregressive drafters",
    summary:
      "DeepSeek's own speculative decoding stack ships hardware-aware schedulers and 2–3× token throughput on H800s.",
    href: "/blog/inside-deepseek-dspark-lossless-inference",
  },
  {
    date: "2026-06-30",
    title: "DeepSeek raises $7.4B under a founder-locked LP vehicle",
    summary:
      "50 billion yuan round with 5-year lock-up. Structure keeps investors passive while Liang Wenfeng retains control.",
    href: "/blog/deepseek-raises-7-4-billion-bizarre-deal",
  },
];

const V4Tracker = () => (
  <section className="container mx-auto px-4 pb-20">
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
          <Zap className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h2 className="text-2xl md:text-3xl font-bold">DeepSeek V4 Tracker</h2>
          <p className="text-sm text-muted-foreground">
            Live changelog of model releases, pricing shifts and infrastructure changes.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {updates.map((u) => (
          <Link key={u.href} to={u.href} className="group">
            <Card className="h-full bg-white border-0 shadow-sm hover:shadow-lg transition-shadow">
              <CardContent className="p-5">
                <div className="text-xs font-mono text-primary mb-2">
                  {new Date(u.date).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </div>
                <h3 className="font-semibold mb-2 group-hover:text-primary transition-colors">
                  {u.title}
                </h3>
                <p className="text-sm text-muted-foreground mb-3">{u.summary}</p>
                <span className="text-sm text-primary inline-flex items-center gap-1">
                  Read update <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      <div className="mt-6 text-center">
        <Link to="/blog" className="text-sm text-primary hover:underline">
          View full changelog & news archive →
        </Link>
      </div>
    </div>
  </section>
);

export default V4Tracker;
